import { Plan, Charge, Discount } from '@/components/company/types/planTypes';
import { useCompanyData } from '@/hooks/useCompanyData';
import { useEffect, useState } from 'react';

/**
 * Improved GraphQL helper hook that uses React Query as the source of truth
 * Provides utility functions for plans, charges, discounts, and screenshots
 * This replaces the Redux-dependent useGraphQLHelper
 */
export const useGraphQLHelperV2 = (slug: string) => {
  // Use the shared useCompanyData hook to get company data
  const { data: graphQLData, isLoading, error, refetch } = useCompanyData({
    slug,
    enabled: !!slug
  });
  
  /**
   * Check if a plan exists in a specific version
   */
  const isPlanInVersion = (planId: string | null | undefined, version: string): boolean => {
    if (!planId || !version || !graphQLData?.pages) return false;
    
    // Check each page for the specific version's quarters
    for (const page of graphQLData.pages) {
      if (!page.quarters) continue;
      
      // Find the quarter with the matching version
      const quarter = page.quarters.find(q => q.version === version);
      if (!quarter || !quarter.ids) continue;
      
      // Check if the planId exists in the ids array
      if (quarter.ids.includes(planId)) {
        return true;
      }
    }
    
    return false;
  };
  
  /**
   * Check if a charge exists in a specific version for a given plan
   */
  const isChargeInVersion = (planId: string | null | undefined, chargeId: string | null | undefined, version: string): boolean => {
    if (!graphQLData || !graphQLData.pages || !planId || !chargeId || !version) return false;
    
    // First, check if the charge has a direct versions array that includes this version
    // This is the data structure we update with our Redux action
    for (const page of graphQLData.pages) {
      if (!page.plans) continue;
      
      for (const plan of page.plans) {
        if (plan.id !== planId || !plan.charges) continue;
        
        // Find the charge in this plan
        const charge = plan.charges.find(c => c.id === chargeId);
        
        // Check if the charge matches the version
        if (charge && (!charge.version && !(charge.versions as string[] || []).includes(version)) || charge.version === version) {
          return true;
        }
      }
    }
    
    // Fallback: Check each page for the specific version's quarters
    // This is the legacy data structure
    for (const page of graphQLData.pages) {
      if (!page.quarters) continue;
      
      // Find the quarter with the matching version
      const quarter = page.quarters.find(q => q.version === version);
      if (!quarter || !quarter.ids) continue;
      
      // Check if the chargeId exists in the ids array
      if (quarter.ids.includes(chargeId)) {
        return true;
      }
    }
    
    return false;
  };
  
  /**
   * Check if a discount exists in a specific version for a given plan
   */
  const isDiscountInVersion = (planId: string | null | undefined, discountId: string | null | undefined, version: string): boolean => {
    if (!planId || !discountId || !version || !graphQLData?.pages) {
      console.log(`isDiscountInVersion check failed: missing data, planId=${planId}, discountId=${discountId}, version=${version}`);
      return false;
    }
    
    // Important: The current GraphQL schema doesn't include discounts in the query
    // However, discount IDs might be included in the quarters.ids array
    
    // Check each page for the specific version's quarters
    for (const page of graphQLData.pages) {
      if (!page.quarters) continue;
      
      // Find the quarter with the matching version
      const quarter = page.quarters.find(q => q.version === version);
      if (!quarter || !quarter.ids) continue;
      
      // Check if the discountId exists in the ids array
      if (quarter.ids.includes(discountId)) {
        console.log(`Discount ${discountId} found in version ${version}`);
        return true;
      }
    }
    
    return false;
  };
  
  /**
   * Check if a screenshot is invalid for a specific version
   */
  const isScreenshotInvalid = (version: string): boolean => {
    if (!version || !graphQLData?.pages) return false;
    
    // Check all pages for invalid screenshots in this version
    for (const page of graphQLData.pages) {
      // Check quarters data
      const quarters = page.quarters || [];
      const quarter = quarters.find(q => q.version === version);
      
      // If quarter exists but no screenshot_url or it's explicitly marked unverified
      if (quarter && (!quarter.screenshot_url || quarter.verified === false)) {
        return true;
      }
      
      // Also check scrapes data
      const scrapes = page.scrapes || [];
      const scrape = scrapes.find(s => s.version === version);
      
      // If scrape exists but no screenshot_url or it's explicitly marked unverified
      if (scrape && (!scrape.screenshot_url || scrape.verified === false)) {
        return true;
      }
    }
    
    return false;
  };
  
  /**
   * Get all unique charges for a specific plan
   */
  const getUniqueChargesForPlan = (planId: string | null | undefined): Charge[] => {
    if (!planId || !graphQLData?.pages) return [];
    
    const uniqueCharges = new Map<string, Charge>();
    
    // Check each page for the plan and collect unique charges
    for (const page of graphQLData.pages) {
      if (!page.plans) continue;
      
      // Find the plan
      const plan = page.plans.find(p => p.id === planId);
      if (!plan || !plan.charges) continue;
      
      // Helper to transform a charge to our expected format
      const transformCharge = (charge: any): any => {
        return {
          ...charge,
          unit: charge.unit || '',
          metric_name: charge.metric_name || charge.metric?.name || '',
          metric_id: charge.metric_id || charge.metric?.id || '',
          price_per: charge.price_per ?? 0,  // Ensure required fields exist
        };
      };
      
      // Add all charges to our map, using id as the key to ensure uniqueness
      for (const charge of plan.charges) {
        if (charge.id && !uniqueCharges.has(charge.id)) {
          uniqueCharges.set(charge.id, transformCharge(charge));
        }
      }
    }
    
    return Array.from(uniqueCharges.values());
  };
  
  /**
   * Get unique discounts for a given plan
   */
  const getUniqueDiscountsForPlan = (planId: string | null | undefined): Discount[] => {
    if (!planId || !graphQLData?.pages) return [];
    
    const uniqueDiscountsMap = new Map<string, Discount>();
    
    // Check each page for the plan
    for (const page of graphQLData.pages) {
      if (!page.plans) continue;
      
      // Find the plan
      const plan = page.plans.find(p => p.id === planId);
      if (!plan || !plan.discounts) continue;
      
      // Helper to transform a discount to our expected format
      const transformDiscount = (discount: any): any => {
        return {
          ...discount,
          amount: discount.amount || 0,
          type: discount.type || 'percentage',
          description: discount.description || '',  // Ensure required fields exist
        };
      };
      
      // Add each discount to our map to deduplicate
      plan.discounts.forEach(discount => {
        if (discount && discount.id) {
          uniqueDiscountsMap.set(discount.id, transformDiscount(discount));
        }
      });
    }
    
    const discounts = Array.from(uniqueDiscountsMap.values());
    console.log(`Found ${discounts.length} discounts for plan ${planId}`);
    return discounts;
  };

  /**
   * Get a plan by its ID
   */
  const getPlanById = (planId: string | null | undefined): Plan | null => {
    if (!planId || !graphQLData?.pages) return null;
    
    // Check each page for the plan
    for (const page of graphQLData.pages) {
      if (!page.plans) continue;
      
      // Find and return the plan
      const plan = page.plans.find(p => p.id === planId);
      if (plan) return plan;
    }
    
    return null;
  };

  /**
   * Get all plans from all pages
   */
  const getAllPlans = (): Plan[] => {
    if (!graphQLData?.pages) return [];
    
    const allPlans: Plan[] = [];
    
    // Collect plans from all pages
    for (const page of graphQLData.pages) {
      if (page.plans && page.plans.length > 0) {
        allPlans.push(...page.plans);
      }
    }
    
    return allPlans;
  };
  
  /**
   * Update a plan with new data
   */
  const updatePlan = (plan: any, newPlanData: any) => {
    if (!graphQLData?.pages) return;

    const newData = { ...graphQLData };
    const pageIndex = newData.pages.findIndex((p: any) => p.id === plan.pageId);
    
    if (pageIndex !== -1) {
      const planIndex = newData.pages[pageIndex].plans.findIndex((p: any) => p.id === plan.id);
      if (planIndex !== -1) {
        newData.pages[pageIndex].plans[planIndex] = newPlanData;
      }
    }
    
    // setGraphQLData(newData); // This line is commented out because setGraphQLData is not defined in this context
  };

  /**
   * Get all plans with their charges/discounts
   */
  const getPlans = (version?: string) => {
    if (!graphQLData?.pages) return [];

    const plansArray: Plan[] = [];
    
    graphQLData.pages.forEach((page: any) => {
      if (page.plans && Array.isArray(page.plans)) {
        const plansToAdd = page.plans
          .filter((plan: any) => !version || isPlanInVersion(plan, version))
          .map((plan: any) => {
            return {
              ...plan,
              charges: (plan.charges || []).map((charge: any) => ({
                ...charge,
                // Ensure required fields exist
                price_per: charge.price_per ?? 0,
                unit: charge.unit ?? '',
                metric_id: charge.metric_id ?? charge.metric?.id ?? '',
                metric_name: charge.metric_name ?? charge.metric?.name ?? '',
              })),
              discounts: (plan.discounts || []).map((discount: any) => ({
                ...discount,
                // Ensure required fields exist
                description: discount.description ?? '',
                amount: discount.amount ?? 0,
              })),
              pageId: page.id,
            };
          });
        
        plansArray.push(...plansToAdd);
      }
    });
    
    return plansArray;
  };  

  return {
    isPlanInVersion,
    isChargeInVersion,
    isDiscountInVersion,
    isScreenshotInvalid,
    getUniqueChargesForPlan,
    getUniqueDiscountsForPlan,
    getPlanById,
    getAllPlans,
    updatePlan,
    getPlans,
    graphQLData,
    isLoading,
    error,
    refetch
  };
};

export default useGraphQLHelperV2;
